/**
 * EngagementPicker — dropdown bound to the EngagementContext.
 *
 * Below md (768px) renders as a compact pill that opens a fullscreen
 * modal with search ; at md+ keeps the native <select> (Phase 9 Y5 #165).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { useEngagement } from "../hooks/useEngagement";

const ALL_OPTION = {
  id: "",
  name: "Tous les engagements",
  kind: "all",
  status: "",
} as const;

export function EngagementPicker() {
  const { engagementId, setEngagementId, engagements, loading } = useEngagement();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Inject unknown id so the visible label stays consistent.
  const known = engagementId === "" || engagements.some((e) => e.id === engagementId);
  const options = useMemo(
    () => [
      ALL_OPTION,
      ...(known
        ? engagements
        : [{ id: engagementId, name: engagementId, kind: "?", status: "?" }, ...engagements]),
    ],
    [engagementId, engagements, known],
  );

  const current = options.find((e) => e.id === engagementId) ?? ALL_OPTION;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (e) => e.name.toLowerCase().includes(q) || (e.id ?? "").toLowerCase().includes(q),
    );
  }, [options, query]);

  // Body scroll lock + Escape + focus search on open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const select = (id: string) => {
    setEngagementId(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <>
      {/* Mobile — compact pill trigger (< md) */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        disabled={loading && engagements.length === 0}
        aria-label={`Engagement courant : ${current.name}. Changer`}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="md:hidden inline-flex items-center gap-1.5 max-w-[160px] px-3 py-2 rounded-md
                   bg-[var(--color-bg-card)] border border-[var(--color-border)]
                   text-[var(--color-text)] text-xs font-mono
                   hover:border-[var(--color-border-strong)] transition-colors
                   disabled:opacity-50"
      >
        <span className="truncate">{current.name}</span>
        <ChevronDown className="size-3.5 shrink-0 text-[var(--color-text-dim)]" strokeWidth={1.5} />
      </button>

      {/* Desktop — native <select> (≥ md) */}
      <label className="hidden md:flex items-center gap-2 text-[var(--color-text-muted)]">
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">
          Engagement
        </span>
        <select
          value={engagementId}
          onChange={(e) => setEngagementId(e.target.value)}
          disabled={loading && engagements.length === 0}
          className="font-mono text-xs px-3 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
          style={{
            background: "var(--color-bg-card)",
            color: "var(--color-text)",
            border: "1px solid var(--color-border)",
            minWidth: "180px",
          }}
        >
          {options.map((e) => (
            <option key={e.id || "_all"} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>
      </label>

      {/* Mobile modal */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Sélectionner l'engagement"
          className="md:hidden fixed inset-0 z-50 flex flex-col bg-[var(--color-bg)]"
        >
          <div className="sticky top-0 flex items-center gap-2 px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fermer"
              className="size-11 -ml-2 grid place-items-center rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/5 transition-colors"
            >
              <X className="size-5" strokeWidth={1.5} aria-hidden="true" />
            </button>
            <h2 className="flex-1 font-display italic text-xl">
              Engagement
            </h2>
          </div>
          <div className="px-4 py-3 border-b border-[var(--color-border)]">
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher…"
              aria-label="Rechercher un engagement"
              className="w-full h-12 px-3 rounded-md bg-[var(--color-bg-card)] border border-[var(--color-border)] text-base text-[var(--color-text)] outline-none focus:border-[var(--color-accent)]"
            />
          </div>
          <ul className="flex-1 overflow-y-auto divide-y divide-[var(--color-border)]">
            {filtered.length === 0 && (
              <li className="px-4 py-6 text-sm text-[var(--color-text-dim)] text-center">
                Aucun engagement trouvé
              </li>
            )}
            {filtered.map((e) => {
              const active = e.id === engagementId;
              return (
                <li key={e.id || "_all"}>
                  <button
                    type="button"
                    onClick={() => select(e.id)}
                    aria-current={active ? "true" : undefined}
                    className={`w-full min-h-[56px] flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      active
                        ? "bg-[var(--color-accent-soft)] text-[var(--color-accent-glow)]"
                        : "hover:bg-white/[0.02] text-[var(--color-text)]"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-medium">{e.name}</div>
                      {e.id && (
                        <div className="truncate font-mono text-[11px] text-[var(--color-text-dim)] mt-0.5">
                          {e.id}
                        </div>
                      )}
                    </div>
                    {active && (
                      <span className="size-2 rounded-full bg-[var(--color-accent)] shadow-[0_0_6px_var(--color-accent)] shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}
